import { resolveLocale } from '../utils/locale.js';
import { reportContactFormConversion } from '../utils/google-ads.js';
import { submitContactForm } from '../api/contact.js';

const FORM_MESSAGES = {
  tr: {
    sending: 'Gonderiliyor...',
    success: 'Form basariyla gonderildi.',
    error: 'Gonderim basarisiz oldu. Lutfen tekrar deneyin.',
    filesSelected: (count) => `${count} dosya secildi`,
    validation: {
      required: 'Bu alan zorunludur.',
      name: 'Lutfen ad soyad bilginizi girin.',
      email: 'Lutfen gecerli bir e-posta adresi girin.',
      phone: 'Lutfen gecerli bir telefon numarasi girin.',
      message: 'Mesaj en az 5 karakter olmalidir.',
      privacy: 'Devam etmek icin onay kutusunu isaretleyin.',
    },
  },
  en: {
    sending: 'Sending...',
    success: 'Form submitted successfully.',
    error: 'Submission failed. Please try again.',
    filesSelected: (count) => `${count} files selected`,
    validation: {
      required: 'This field is required.',
      name: 'Please enter your full name.',
      email: 'Please enter a valid email address.',
      phone: 'Please enter a valid phone number.',
      message: 'Message must be at least 5 characters.',
      privacy: 'Please accept the checkbox to continue.',
    },
  },
};

const getFormMessages = () => FORM_MESSAGES[resolveLocale()] || FORM_MESSAGES.tr;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_DIGITS_REGEX = /\d/g;

const getTrimmedValue = (field) => (field?.value || '').trim();
const getPhoneDigits = (value) => (value.match(PHONE_DIGITS_REGEX) || []).join('');

const validateField = (field, messages) => {
  if (!field) return true;

  const value = getTrimmedValue(field);
  let errorMessage = '';

  if (field.name === 'privacy') {
    if (!field.checked) {
      errorMessage = messages.validation.privacy;
    }
  } else if (field.name === 'name') {
    if (!value) {
      errorMessage = messages.validation.required;
    } else if (value.length < 2) {
      errorMessage = messages.validation.name;
    }
  } else if (field.name === 'email') {
    if (!value) {
      errorMessage = messages.validation.required;
    } else if (!EMAIL_REGEX.test(value)) {
      errorMessage = messages.validation.email;
    }
  } else if (field.name === 'phone') {
    const phoneDigits = getPhoneDigits(value);
    if (!phoneDigits) {
      errorMessage = messages.validation.required;
    } else if (phoneDigits.length < 7) {
      errorMessage = messages.validation.phone;
    }
  } else if (field.name === 'message') {
    if (!value) {
      errorMessage = messages.validation.required;
    } else if (value.length < 5) {
      errorMessage = messages.validation.message;
    }
  } else if (field.required && !value) {
    errorMessage = messages.validation.required;
  }

  field.setCustomValidity(errorMessage);
  return !errorMessage;
};

export const initContactForms = () => {
  const forms = document.querySelectorAll('[data-contact-form]');
  const messages = getFormMessages();

  forms.forEach((form) => {
    const countryToggle = form.querySelector('.phone-country');
    const countryList = form.querySelector('.phone-country-list');
    const countryOptions = Array.from(form.querySelectorAll('.phone-country-option'));
    const countryCode = form.querySelector('.phone-code');
    const countryFlag = form.querySelector('.phone-flag use');
    const countryValue = form.querySelector('.phone-country-value');
    const fileInput = form.querySelector('.file-input');
    const fileButton = form.querySelector('.file-btn');
    const fileText = form.querySelector('.file-text');
    const textarea = form.querySelector('.textarea-field');
    const charCount = form.querySelector('.char-count');
    const submitButton = form.querySelector('button[type="submit"]');
    const clearButton = form.querySelector('.form-actions button[type="button"]');
    const status = form.querySelector('.form-status');
    const validationFields = Array.from(form.querySelectorAll('input, textarea, select'));

    const initialFileText = fileText?.textContent || '';
    const initialCountry = {
      display: countryCode?.textContent || '',
      flag: countryFlag?.getAttribute('href') || '',
      code: countryValue?.value || '',
    };

    const setStatus = (message, isError = false) => {
      if (!status) return;
      status.textContent = message;
      status.style.color = isError ? 'var(--color-error-text, #B42318)' : '';
    };

    const runValidation = () => {
      validationFields.forEach((field) => {
        validateField(field, messages);
      });
      return form.checkValidity();
    };

    const updateSubmitState = () => {
      if (!submitButton) return;
      submitButton.disabled = !runValidation();
    };

    const setDropdownOpen = (isOpen) => {
      if (!countryToggle) return;
      countryToggle.classList.toggle('open', isOpen);
      countryToggle.dataset.open = isOpen ? 'true' : 'false';
      countryToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    };

    const setCountry = (option) => {
      if (!option) return;
      const display = option.dataset.display || option.textContent?.trim() || '';
      const flag = option.dataset.flag;
      const code = option.dataset.code || option.dataset.display || '';

      if (countryCode) {
        countryCode.textContent = display;
      }

      if (countryFlag && flag) {
        countryFlag.setAttribute('href', `/assets/images/${flag}.svg#${flag}`);
      }

      if (countryValue) {
        countryValue.value = code || display;
      }

      countryOptions.forEach((entry) => entry.classList.toggle('active', entry === option));
    };

    if (countryToggle && countryList) {
      countryToggle.addEventListener('click', (event) => {
        if (event.target.closest('.phone-country-list')) return;
        const isOpen = countryToggle.dataset.open === 'true';
        setDropdownOpen(!isOpen);
      });

      countryOptions.forEach((option) => {
        option.addEventListener('click', () => {
          setCountry(option);
          setDropdownOpen(false);
        });
      });

      document.addEventListener('click', (event) => {
        if (!countryToggle.contains(event.target)) {
          setDropdownOpen(false);
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          setDropdownOpen(false);
        }
      });
    }

    if (fileButton && fileInput) {
      fileButton.addEventListener('click', () => {
        fileInput.click();
      });
    }

    if (fileInput && fileText) {
      fileInput.addEventListener('change', () => {
        if (!fileInput.files || fileInput.files.length === 0) {
          fileText.textContent = initialFileText;
          return;
        }

        if (fileInput.files.length === 1) {
          fileText.textContent = fileInput.files[0].name;
          return;
        }

        fileText.textContent = messages.filesSelected(fileInput.files.length);
      });
    }

    const updateCharCount = () => {
      if (!textarea || !charCount) return;
      const maxLength = Number(textarea.getAttribute('maxlength')) || 0;
      const current = textarea.value.length;
      if (maxLength > 0) {
        charCount.textContent = `${current}/${maxLength}`;
      } else {
        charCount.textContent = `${current}`;
      }
    };

    if (textarea && charCount) {
      textarea.addEventListener('input', updateCharCount);
      updateCharCount();
    }

    const requiredFields = Array.from(form.querySelectorAll('[required]'));
    requiredFields.forEach((field) => {
      field.addEventListener('input', updateSubmitState);
      field.addEventListener('change', updateSubmitState);
      field.addEventListener('blur', updateSubmitState);
    });
    updateSubmitState();

    if (clearButton) {
      clearButton.addEventListener('click', () => {
        form.reset();
        if (fileText) {
          fileText.textContent = initialFileText;
        }
        if (countryCode) {
          countryCode.textContent = initialCountry.display;
        }
        if (countryFlag && initialCountry.flag) {
          countryFlag.setAttribute('href', initialCountry.flag);
        }
        if (countryValue) {
          countryValue.value = initialCountry.code || initialCountry.display;
        }
        const activeOption = countryOptions.find((option) => option.classList.contains('active'));
        if (activeOption) {
          setCountry(activeOption);
        }
        setStatus('');
        updateCharCount();
        updateSubmitState();
      });
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      if (!runValidation()) {
        form.reportValidity();
        updateSubmitState();
        return;
      }

      const originalText = submitButton?.textContent || '';
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = messages.sending;
      }
      setStatus(messages.sending);

      try {
        await submitContactForm(form);
        reportContactFormConversion();

        form.reset();
        if (fileText) {
          fileText.textContent = initialFileText;
        }
        if (countryCode) {
          countryCode.textContent = initialCountry.display;
        }
        if (countryFlag && initialCountry.flag) {
          countryFlag.setAttribute('href', initialCountry.flag);
        }
        if (countryValue) {
          countryValue.value = initialCountry.code || initialCountry.display;
        }
        updateCharCount();
        setStatus(messages.success);
      } catch (error) {
        setStatus(messages.error, true);
      } finally {
        if (submitButton) {
          submitButton.textContent = originalText;
          updateSubmitState();
        }
      }
    });
  });
};
